import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '../firebase';

// Handles the links in Firebase Auth emails (password reset, email verification,
// email change undo). Firebase sends people here once the project's action URL
// is set to https://odikai.bet/auth/action (see email-templates/README.md).

const EXPIRED = 'This link has expired or has already been used.';

function friendlyError(err) {
  switch (err && err.code) {
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      return EXPIRED;
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact us if you think that is a mistake.';
    case 'auth/user-not-found':
      return 'We could not find an account for this link.';
    case 'auth/weak-password':
      return 'Choose a stronger password: at least 6 characters.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function Shell({ title, lede, children }) {
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{title}</h2>
        {lede && <p className="auth-lede">{lede}</p>}
        {children}
      </div>
    </div>
  );
}

function BackLinks({ resetLink = false }) {
  return (
    <div className="auth-links">
      <Link to="/login">Go to log in</Link>
      {resetLink && <Link to="/forgot-password">Send a new reset link</Link>}
    </div>
  );
}

function ResetPassword({ code }) {
  const [state, setState] = useState('checking'); // checking | form | done | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    verifyPasswordResetCode(auth, code)
      .then((addr) => {
        setEmail(addr);
        setState('form');
      })
      .catch((err) => {
        setError(friendlyError(err));
        setState('error');
      });
  }, [code]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError('The passwords do not match.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await confirmPasswordReset(auth, code, password);
      setState('done');
    } catch (err) {
      setError(friendlyError(err));
      if (friendlyError(err) === EXPIRED) setState('error');
    } finally {
      setSaving(false);
    }
  }

  if (state === 'checking') return <Shell title="Reset password" lede="Checking your link…" />;
  if (state === 'error') {
    return (
      <Shell title="Link expired" lede={error}>
        <BackLinks resetLink />
      </Shell>
    );
  }
  if (state === 'done') {
    return (
      <Shell title="Password updated" lede="You can now log in with your new password.">
        <Link to="/login" className="btn btn-primary">Log in</Link>
      </Shell>
    );
  }
  return (
    <Shell title="Choose a new password" lede={<>For <strong>{email}</strong>.</>}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="reset-confirm">Confirm new password</label>
          <input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        <button disabled={saving} type="submit" className="btn btn-primary">
          {saving ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </Shell>
  );
}

function VerifyEmail({ code }) {
  const [state, setState] = useState('checking');
  const [error, setError] = useState('');

  useEffect(() => {
    applyActionCode(auth, code)
      .then(async () => {
        // Refresh the signed-in user so emailVerified is true straight away.
        if (auth.currentUser) {
          await auth.currentUser.reload().catch(() => {});
          await auth.currentUser.getIdToken(true).catch(() => {});
        }
        setState('done');
      })
      .catch((err) => {
        setError(friendlyError(err));
        setState('error');
      });
  }, [code]);

  if (state === 'checking') return <Shell title="Verifying email" lede="One moment…" />;
  if (state === 'error') {
    return (
      <Shell title="Link expired" lede={`${error} Request a new verification email and try again.`}>
        <BackLinks />
      </Shell>
    );
  }
  return (
    <Shell title="Email verified" lede="Thanks. Your email address is confirmed.">
      <Link to="/" className="btn btn-primary">Continue to Odikai</Link>
    </Shell>
  );
}

function RecoverEmail({ code }) {
  const [state, setState] = useState('checking');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    checkActionCode(auth, code)
      .then((info) => {
        setEmail(info.data.email || '');
        return applyActionCode(auth, code);
      })
      .then(() => setState('done'))
      .catch((err) => {
        setError(friendlyError(err));
        setState('error');
      });
  }, [code]);

  async function sendReset() {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  if (state === 'checking') return <Shell title="Restoring email" lede="One moment…" />;
  if (state === 'error') {
    return (
      <Shell title="Link expired" lede={error}>
        <BackLinks />
      </Shell>
    );
  }
  return (
    <Shell
      title="Email restored"
      lede={<>Your sign-in email is back to <strong>{email}</strong>. If you didn't make the change, reset your password now as well.</>}
    >
      {error && <div className="alert alert-error">{error}</div>}
      {resetSent && <div className="alert alert-success">Password reset link sent to {email}.</div>}
      <button type="button" className="btn btn-primary" onClick={sendReset} disabled={resetSent}>
        Reset my password
      </button>
      <BackLinks />
    </Shell>
  );
}

export default function AuthAction() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const code = params.get('oobCode');

  if (!code) {
    return (
      <Shell title="Invalid link" lede="This link is missing information. Open it straight from the email we sent you.">
        <BackLinks />
      </Shell>
    );
  }
  switch (mode) {
    case 'resetPassword':
      return <ResetPassword code={code} />;
    case 'verifyEmail':
    case 'verifyAndChangeEmail':
      return <VerifyEmail code={code} />;
    case 'recoverEmail':
      return <RecoverEmail code={code} />;
    default:
      return (
        <Shell title="Invalid link" lede="We don't recognise this link. Open it straight from the email we sent you.">
          <BackLinks />
        </Shell>
      );
  }
}
